/**
 * Smart Field Mapper — The backbone of the connector marketplace.
 *
 * Auto-detects CSV/Excel column headers by fuzzy-matching against known
 * RetailsyncGo field definitions. Uses multiple strategies:
 *   1. Exact match
 *   2. Case-insensitive match
 *   3. Alias/synonym lookup
 *   4. Levenshtein distance fuzzy match
 *   5. Pattern detection (price columns look like prices, etc.)
 *
 * If field mapping fails, the marketplace fails. This is our moat.
 */

// ─── Field Definitions ──────────────────────────────────────────────────────

export interface TargetField {
  key: string
  label: string
  aliases: string[]          // Common alternative names
  required: boolean
  type: 'string' | 'number' | 'currency' | 'date' | 'boolean'
  patterns?: RegExp[]        // Value-level patterns for detection
  examples?: string[]        // Sample values that match this field
}

export interface AutoMapping {
  sourceHeader: string
  targetField: string
  confidence: number        // 0–100
  method: 'exact' | 'alias' | 'fuzzy' | 'pattern'
}

export interface MappingReport {
  detected: AutoMapping[]
  unmapped: string[]        // Source headers we couldn't map
  missing: TargetField[]    // Required fields with no mapping found
  coverage: number          // 0–100 percentage of headers mapped
  suggestions: string       // Human-readable summary
}

// ─── Entity Field Definitions ───────────────────────────────────────────────

const PRODUCT_FIELDS: TargetField[] = [
  { key: 'name', label: 'Product Name', aliases: ['name', 'product name', 'product_name', 'title', 'item name', 'item_name', 'description'], required: true, type: 'string' },
  { key: 'sku', label: 'SKU', aliases: ['sku', 'product sku', 'product_sku', 'item sku', 'item_sku', 'code', 'product code', 'product_code', 'item code', 'item_code', 'stock code', 'stock_code', 'plu', 'upc'], required: true, type: 'string' },
  { key: 'price', label: 'Price', aliases: ['price', 'selling price', 'selling_price', 'retail price', 'retail_price', 'unit price', 'unit_price', 'sales price', 'sales_price', 'amount', 'rate'], required: true, type: 'currency', patterns: [/^\d+(\.\d{1,2})?$/] },
  { key: 'cost_price', label: 'Cost Price', aliases: ['cost', 'cost price', 'cost_price', 'purchase price', 'purchase_price', 'buying price', 'buying_price', 'wholesale price', 'wholesale_price', 'supplier price'], required: false, type: 'currency' },
  { key: 'category', label: 'Category', aliases: ['category', 'product category', 'product_category', 'type', 'group', 'product group', 'product_group', 'class'], required: false, type: 'string' },
  { key: 'brand', label: 'Brand', aliases: ['brand', 'manufacturer', 'make', 'supplier', 'vendor'], required: false, type: 'string' },
  { key: 'barcode', label: 'Barcode', aliases: ['barcode', 'bar code', 'bar_code', 'ean', 'upc', 'gtin', 'isbn', 'scan code', 'scan_code'], required: false, type: 'string' },
  { key: 'unit', label: 'Unit of Measure', aliases: ['unit', 'uom', 'unit of measure', 'unit_of_measure', 'measure', 'packaging', 'unit type', 'unit_type', 'pack size', 'pack_size'], required: false, type: 'string' },
  // `products.department` is its OWN column (20261001000001_products_department.sql —
  // "distinct from category_id (structured hierarchy)"). It used to be an alias of `category`
  // here, which filed a department name as a category and auto-created a category for it.
  { key: 'department', label: 'Department', aliases: ['department', 'dept', 'section', 'division', 'product department', 'product_department'], required: false, type: 'string' },
  { key: 'stock', label: 'Stock Quantity', aliases: ['stock', 'stock quantity', 'stock_quantity', 'quantity', 'qty', 'in stock', 'in_stock', 'stock level', 'stock_level', 'on hand', 'on_hand', 'available', 'inventory', 'balance', 'count', 'units', 'current stock', 'current_stock'], required: false, type: 'number' },
  { key: 'min_stock_level', label: 'Min Stock Level', aliases: ['min stock', 'min_stock', 'minimum stock', 'minimum_stock', 'reorder level', 'reorder_level', 'reorder point', 'reorder_point', 'min level', 'min_level', 'low stock', 'low_stock', 'alert level'], required: false, type: 'number' },
  { key: 'max_stock_level', label: 'Max Stock Level', aliases: ['max stock', 'max_stock', 'maximum stock', 'maximum_stock', 'max level', 'max_level', 'max qty', 'max_qty', 'reorder max'], required: false, type: 'number' },
  // `products.bulk_min_quantity` / `bulk_price` are their own columns (20260710000000_).
  // `bulk price` used to be an alias of `wholesale_price`, so a wholesale tier and a bulk tier
  // collapsed into one field and one of them was always wrong.
  { key: 'bulk_min_quantity', label: 'Bulk Min Quantity', aliases: ['bulk min quantity', 'bulk_min_quantity', 'bulk quantity', 'bulk_quantity', 'bulk qty', 'bulk_qty', 'min bulk qty', 'bulk break qty', 'bulk_min_qty'], required: false, type: 'number' },
  { key: 'bulk_price', label: 'Bulk Price', aliases: ['bulk price', 'bulk_price', 'bulk rate', 'bulk_rate', 'bulk selling price'], required: false, type: 'currency' },
  { key: 'description', label: 'Description', aliases: ['description', 'desc', 'details', 'info', 'notes', 'summary', 'specification'], required: false, type: 'string' },
  { key: 'batch_number', label: 'Batch Number', aliases: ['batch', 'batch number', 'batch_number', 'lot', 'lot number', 'lot_number', 'batch no', 'batch_no', 'lot no'], required: false, type: 'string' },
  { key: 'expiry_date', label: 'Expiry Date', aliases: ['expiry', 'expiry date', 'expiry_date', 'expiration', 'expiration date', 'expiration_date', 'exp date', 'exp_date', 'use by', 'use_by', 'best before', 'best_before'], required: false, type: 'date' },
  // `products.sell_by` is the UNIT OF SALE — the cloud CHECK allows only
  // unit/pack/case/kg/liter/bulk (20260710000000_). It used to be an alias of `expiry_date`,
  // which sent values like "pack" straight into a DATE column.
  { key: 'sell_by', label: 'Sell By (unit of sale)', aliases: ['sell by', 'sell_by', 'sold by', 'sold_by', 'sale unit', 'sale_unit', 'pricing basis', 'pricing_basis'], required: false, type: 'string' },
  { key: 'item_type', label: 'Item Type', aliases: ['item type', 'item_type'], required: false, type: 'string' },
  { key: 'show_online', label: 'Show Online', aliases: ['show online', 'show_online', 'online', 'web visible', 'web_visible', 'publish online', 'show in store'], required: false, type: 'boolean' },
  { key: 'requires_prescription', label: 'Requires Prescription', aliases: ['requires prescription', 'requires_prescription', 'prescription required', 'rx only', 'rx_only', 'rx_required', 'controlled drug'], required: false, type: 'boolean' },
  { key: 'manufacturer', label: 'Manufacturer', aliases: ['manufacturer', 'maker', 'producer', 'factory'], required: false, type: 'string' },
  { key: 'wholesale_price', label: 'Wholesale Price', aliases: ['wholesale', 'wholesale price', 'trade price', 'trade_price', 'dealer price', 'dealer_price'], required: false, type: 'currency' },
  { key: 'is_active', label: 'Active', aliases: ['active', 'is active', 'is_active', 'status', 'enabled', 'visible'], required: false, type: 'boolean' },
]

const CUSTOMER_FIELDS: TargetField[] = [
  { key: 'name', label: 'Full Name', aliases: ['name', 'full name', 'full_name', 'customer name', 'customer_name', 'contact name', 'contact_name', 'client', 'client name'], required: true, type: 'string' },
  { key: 'first_name', label: 'First Name', aliases: ['first name', 'first_name', 'firstname', 'given name', 'given_name'], required: false, type: 'string' },
  { key: 'last_name', label: 'Last Name', aliases: ['last name', 'last_name', 'lastname', 'surname', 'family name', 'family_name'], required: false, type: 'string' },
  { key: 'email', label: 'Email', aliases: ['email', 'e-mail', 'email address', 'email_address', 'mail', 'contact email'], required: false, type: 'string', patterns: [/@/] },
  { key: 'phone', label: 'Phone', aliases: ['phone', 'phone number', 'phone_number', 'telephone', 'mobile', 'cell', 'contact', 'tel', 'contact number'], required: false, type: 'string' },
  { key: 'address', label: 'Address', aliases: ['address', 'street', 'location', 'physical address', 'physical_address', 'residence'], required: false, type: 'string' },
  { key: 'city', label: 'City', aliases: ['city', 'town', 'municipality'], required: false, type: 'string' },
  { key: 'state', label: 'State', aliases: ['state', 'province', 'region', 'county'], required: false, type: 'string' },
  { key: 'country', label: 'Country', aliases: ['country', 'nation'], required: false, type: 'string' },
  { key: 'customer_type', label: 'Customer Type', aliases: ['type', 'customer type', 'customer_type', 'client type', 'client_type', 'category', 'segment', 'group'], required: false, type: 'string' },
  { key: 'date_of_birth', label: 'Date of Birth', aliases: ['dob', 'date of birth', 'date_of_birth', 'birth date', 'birth_date', 'birthday'], required: false, type: 'date' },
  { key: 'gender', label: 'Gender', aliases: ['gender', 'sex'], required: false, type: 'string' },
  { key: 'notes', label: 'Notes', aliases: ['notes', 'note', 'comments', 'remarks', 'memo', 'description'], required: false, type: 'string' },
  { key: 'postal_code', label: 'Postal Code', aliases: ['postal code', 'postal_code', 'postcode', 'post_code', 'zip', 'zip code', 'zip_code', 'pincode'], required: false, type: 'string' },
  { key: 'is_active', label: 'Active', aliases: ['active', 'is active', 'is_active', 'enabled', 'status'], required: false, type: 'boolean' },
]

const INVENTORY_FIELDS: TargetField[] = [
  { key: 'sku', label: 'SKU', aliases: ['sku', 'product sku', 'product_sku', 'item code', 'item_code', 'code'], required: true, type: 'string' },
  { key: 'quantity', label: 'Quantity', aliases: ['quantity', 'qty', 'stock', 'stock_quantity', 'in stock', 'in_stock', 'on hand', 'on_hand', 'available', 'balance', 'count', 'units'], required: true, type: 'number' },
  { key: 'reserved_quantity', label: 'Reserved', aliases: ['reserved', 'reserved quantity', 'reserved_quantity', 'committed', 'allocated', 'ordered'], required: false, type: 'number' },
  { key: 'min_stock_level', label: 'Min Stock', aliases: ['min', 'min stock', 'min_stock', 'minimum', 'reorder', 'reorder level', 'reorder_level'], required: false, type: 'number' },
  { key: 'max_stock_level', label: 'Max Stock', aliases: ['max', 'max stock', 'max_stock', 'maximum', 'maximum stock', 'max level', 'max_level'], required: false, type: 'number' },
  { key: 'branch_name', label: 'Branch', aliases: ['branch', 'branch name', 'branch_name', 'store', 'site', 'outlet'], required: false, type: 'string' },
  // `inventory.warehouse_id` is a UUID FK, so a warehouse NAME resolves to a real warehouse.
  { key: 'warehouse_name', label: 'Warehouse', aliases: ['warehouse', 'warehouse name', 'warehouse_name', 'store name', 'depot', 'godown'], required: false, type: 'string' },
  // The real column is `inventory.location` (20260707000000_). `location_zone` does not exist.
  { key: 'location', label: 'Location / Zone', aliases: ['location', 'location zone', 'location_zone', 'zone', 'bin', 'shelf', 'aisle', 'rack', 'bin location', 'storage location'], required: false, type: 'string' },
  { key: 'batch_number', label: 'Batch Number', aliases: ['batch', 'batch number', 'batch_number', 'lot', 'lot number', 'lot_number'], required: false, type: 'string' },
  { key: 'expiry_date', label: 'Expiry Date', aliases: ['expiry', 'expiry date', 'expiry_date', 'expiration', 'expiration_date', 'best before', 'best_before'], required: false, type: 'date' },
  // `inventory.available_quantity` is GENERATED ALWAYS (quantity - reserved_quantity), so it is
  // deliberately absent: mapping a column to it would promise a write the database refuses.
]

const TRANSACTION_FIELDS: TargetField[] = [
  { key: 'product_sku', label: 'Product SKU', aliases: ['sku', 'product sku', 'product_sku', 'item', 'item code', 'item_code', 'code'], required: true, type: 'string' },
  { key: 'quantity', label: 'Quantity', aliases: ['quantity', 'qty', 'units', 'count', 'pieces'], required: true, type: 'number' },
  { key: 'unit_price', label: 'Unit Price', aliases: ['price', 'unit price', 'unit_price', 'rate', 'selling price', 'selling_price', 'amount'], required: true, type: 'currency' },
  { key: 'total', label: 'Total', aliases: ['total', 'total price', 'total_price', 'total amount', 'total_amount', 'sum', 'line total', 'line_total'], required: false, type: 'currency' },
  { key: 'payment_method', label: 'Payment Method', aliases: ['payment', 'payment method', 'payment_method', 'payment type', 'payment_type', 'tender', 'mode'], required: false, type: 'string' },
  { key: 'customer_email', label: 'Customer Email', aliases: ['customer', 'customer email', 'customer_email', 'email', 'client email', 'client_email'], required: false, type: 'string' },
  { key: 'timestamp', label: 'Date/Time', aliases: ['date', 'time', 'timestamp', 'datetime', 'date time', 'date_time', 'sold at', 'sold_at', 'created', 'created_at', 'transaction date', 'transaction_date'], required: false, type: 'date' },
]

// ─── Field Registry ─────────────────────────────────────────────────────────

const FIELD_REGISTRY: Record<string, TargetField[]> = {
  products: PRODUCT_FIELDS,
  customers: CUSTOMER_FIELDS,
  inventory: INVENTORY_FIELDS,
  transactions: TRANSACTION_FIELDS,
}

// ─── Fuzzy String Matching ──────────────────────────────────────────────────

function levenshteinDistance(a: string, b: string): number {
  const m = a.length, n = b.length
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0))
  for (let i = 0; i <= m; i++) dp[i][0] = i
  for (let j = 0; j <= n; j++) dp[0][j] = j
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] : Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]) + 1
    }
  }
  return dp[m][n]
}

function fuzzyScore(source: string, target: string): number {
  const s = source.toLowerCase().replace(/[_\-\s]+/g, ' ').trim()
  const t = target.toLowerCase().replace(/[_\-\s]+/g, ' ').trim()

  // Exact match
  if (s === t) return 100

  // One contains the other
  if (s.includes(t) || t.includes(s)) return 90

  // Levenshtein ratio
  const maxLen = Math.max(s.length, t.length)
  if (maxLen === 0) return 0
  const dist = levenshteinDistance(s, t)
  const ratio = (1 - dist / maxLen) * 100

  return Math.round(ratio)
}

// ─── Main Auto-Mapping Engine ───────────────────────────────────────────────

export function autoMapFields(
  entity: string,
  headers: string[],
  sampleValues?: Record<string, any[]>
): MappingReport {
  const fields = FIELD_REGISTRY[entity] || PRODUCT_FIELDS
  const detected: AutoMapping[] = []
  const unmapped: string[] = []

  for (const header of headers) {
    let bestMatch: { field: TargetField; score: number; method: AutoMapping['method'] } | null = null

    for (const field of fields) {
      // Strategy 1: Exact match on key
      if (header.toLowerCase() === field.key.toLowerCase()) {
        bestMatch = { field, score: 100, method: 'exact' }
        break
      }

      // Strategy 2: Alias match
      for (const alias of field.aliases) {
        const exact = header.toLowerCase() === alias.toLowerCase()
        if (exact) {
          const score = 100
          if (!bestMatch || score > bestMatch.score) {
            bestMatch = { field, score: 100, method: 'alias' }
          }
          break
        }
      }

      // Strategy 3: Fuzzy match
      for (const alias of field.aliases) {
        const score = fuzzyScore(header, alias)
        if (score > 70 && (!bestMatch || score > bestMatch.score)) {
          bestMatch = { field, score, method: 'fuzzy' }
        }
      }

      // Strategy 4: Pattern match on sample values
      if (field.patterns && sampleValues?.[header]) {
        const values = sampleValues[header].filter(Boolean).slice(0, 5)
        if (values.length > 0) {
          const matchRatio = values.filter(v => field.patterns!.some(p => p.test(String(v)))).length / values.length
          if (matchRatio > 0.5) {
            const score = 65 + Math.round(matchRatio * 20)
            if (!bestMatch || score > bestMatch.score) {
              bestMatch = { field, score, method: 'pattern' }
            }
          }
        }
      }
    }

    if (bestMatch && bestMatch.score >= 60) {
      // Check if this target field is already mapped with higher confidence
      const existing = detected.find(d => d.targetField === bestMatch!.field.key)
      if (!existing || bestMatch.score > existing.confidence) {
        if (existing) {
          // Move the displaced mapping back to unmapped
          unmapped.push(existing.sourceHeader)
          detected.splice(detected.indexOf(existing), 1)
        }
        detected.push({
          sourceHeader: header,
          targetField: bestMatch.field.key,
          confidence: bestMatch.score,
          method: bestMatch.method,
        })
      } else {
        unmapped.push(header)
      }
    } else {
      unmapped.push(header)
    }
  }

  // Find missing required fields
  const mappedTargets = new Set(detected.map(d => d.targetField))
  const missing = fields.filter(f => f.required && !mappedTargets.has(f.key))

  // Coverage score
  const coverage = headers.length > 0
    ? Math.round((detected.length / headers.length) * 100)
    : 0

  // Build human-readable suggestion
  let suggestions = ''
  if (detected.length === headers.length) {
    suggestions = `All ${headers.length} columns mapped successfully.`
  } else if (unmapped.length > 0) {
    suggestions = `${detected.length} of ${headers.length} columns auto-mapped. ${unmapped.length} column(s) need manual mapping: ${unmapped.join(', ')}.`
  }
  if (missing.length > 0) {
    suggestions += ` Missing required fields: ${missing.map(f => f.label).join(', ')}.`
  }

  return { detected, unmapped, missing, coverage, suggestions }
}

// ─── Get Field Display Info ─────────────────────────────────────────────────

export function getFieldByEntity(entity: string): TargetField[] {
  return FIELD_REGISTRY[entity] || PRODUCT_FIELDS
}

export function getFieldByKey(entity: string, key: string): TargetField | undefined {
  const fields = FIELD_REGISTRY[entity] || PRODUCT_FIELDS
  return fields.find(f => f.key === key)
}

// ─── Generate Summary Text ──────────────────────────────────────────────────

export function generateSummaryText(
  entity: string,
  totalRecords: number,
  created: number,
  updated: number,
  skipped: number,
  errors: string[]
): string {
  const entityLabel = entity === 'products' ? 'products' :
    entity === 'customers' ? 'customers' :
    entity === 'inventory' ? 'inventory items' :
    entity === 'transactions' ? 'transactions' : 'records'

  const lines: string[] = [
    `📊 **Import Summary** — ${entityLabel}`,
    `Total rows in file: **${totalRecords}**`,
  ]

  if (created > 0) lines.push(`✅ New ${entityLabel} created: **${created}**`)
  if (updated > 0) lines.push(`🔄 Existing ${entityLabel} updated: **${updated}**`)
  if (skipped > 0) lines.push(`⏭️ Skipped (no changes): **${skipped}**`)
  if (errors.length > 0) {
    lines.push(`❌ Errors: **${errors.length}**`)
    lines.push(`   ${errors.slice(0, 5).join('\n   ')}`)
    if (errors.length > 5) lines.push(`   ... and ${errors.length - 5} more`)
  }

  const netChange = created + updated
  if (netChange === 0) {
    lines.push(`\n⚠️ **Net change: 0** — all records already up to date.`)
  } else {
    lines.push(`\n📈 **Net change: ${netChange}** records affected.`)
  }

  return lines.join('\n')
}
